/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import { graphql, useStaticQuery } from "gatsby"
import * as React from "react"
import { useForm } from "react-hook-form"
import { renderToString } from "react-dom/server"
import { Check } from "react-feather"
import { useWindowWidth } from "@react-hook/window-size"
import { SEO } from "abmg-components"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import FunnelStepScreen from "@components/prequalify/FunnelStepScreen"
import {
  requiredOptionRule,
  requiredRule,
} from "@components/global/UnderlineField"
import Modal from "@components/global/Modal"
import ConsentPopup from "@components/global/funnels/ConsentPopup"

/* -------------------------------------------------------------------------- */
/*                               External Hooks                               */
/* -------------------------------------------------------------------------- */
import useClientSideOnly from "@hooks/use-client-side-only"

/* -------------------------------------------------------------------------- */
/*                                External Data                               */
/* -------------------------------------------------------------------------- */
import formLeadRecipients from "@data/pre-qualify-form-lead-recipients.json"

/* -------------------------------------------------------------------------- */
/*                               External Utils                               */
/* -------------------------------------------------------------------------- */
import { bankersInMBSearch, sortAlphabetically } from "@utils/mb"
import { citiesByState, getBreakpoints } from "@utils/prequalify"

/* -------------------------------------------------------------------------- */
/*                                  Variables                                 */
/* -------------------------------------------------------------------------- */
export const storedFunnelName = `refinance-prequalify-funnel`

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function RefinancePrequalify(props) {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [funnelData, setFunnelData] = React.useState({
    currentStep: 0,
    formData: {},
    passedSteps: {},
  })

  const updateFunnelData = React.useCallback(
    (updatedFunnelData, save) => {
      const newFunnelData = {
        formData: updatedFunnelData.formData,
        currentStep:
          updatedFunnelData.currentStep !== undefined
            ? updatedFunnelData.currentStep
            : funnelData.currentStep,
        passedSteps: updatedFunnelData.passedSteps,
      }
      if (save)
        localStorage.setItem(storedFunnelName, JSON.stringify(newFunnelData))
      setFunnelData(newFunnelData)
    },
    [funnelData],
  )

  const {
    control,
    clearErrors,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors, isValid },
    reset,
    resetField,
  } = useForm({
    mode: "onTouched",
    defaultValues: funnelData?.formData,
  })

  React.useEffect(() => {
    const storedFunnelData = JSON.parse(localStorage.getItem(storedFunnelName))
    if (storedFunnelData) {
      setFunnelData(storedFunnelData)
      reset(storedFunnelData.formData)
    }
  }, [])

  const windowWidth = useWindowWidth()
  const isMounted = useClientSideOnly()
  const breakpoints = getBreakpoints({ windowWidth, isMounted })

  const reactHookFormProps = {
    controller: {
      control,
    },
    clearErrors,
    handleSubmit,
    errors,
    register,
    setValue,
    getValues,
    reset,
    resetField,
  }

  const data = useStaticQuery(graphql`
    query {
      HelloThere: file(
        relativePath: { regex: "/pre-qualify/refi-funnel/hello-there/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      YourWay: file(
        relativePath: { regex: "/pre-qualify/refi-funnel/your-way/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      PointUsHome: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/point-us-home/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      PeopleFirstAlways: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/people-first-always/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      RingRing: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/ring-ring/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
    }
  `)
  const classes = {
    contact: {
      formElement: {
        default: `font-variant-numeric--primary bg-transparent border-b border-grey-8 leading-[35px] [outline:0] text-[17px] text-grey-8 -tracking-[.33px] focus:border-b-pacific-blue `,
        error: `border-sunset-orange-6`,
      },
      label: {
        default: `font-variant-numeric--primary leading-[12px] text-grey-8`,
      },
    },
    radio: {
      fieldsContainer: {
        default: `flex flex-col h-full justify-center`,
      },
      formElement: {
        default: `appearance-none bg-center border border-grey-4 box-border h-[29px]  p-0 rounded-full w-[29px] group-hover:[&:not(:checked)]:border-grey-8 checked:before:content-none checked:border-grey-4  checked:!bg-pacific-blue-2 checked:bg-[image:var(--backgroundImage)]`,
      },
      formElementAllContainer: {
        default: ` h-full `,
      },
      formElementGroup: {
        default: `${
          breakpoints.minTablet ? "h-[65px]" : ""
        } group bg-white border border-grey4 flex gap-[12px] items-center [padding:34px_30px] rounded-[13px] w-full [&:has(:checked)]:bg-atlantic-blue-2 hover:bg-atlantic-blue-2`,
      },
      formElementGroupsContainer: {
        default: `grid ${"content-center grid-auto-rows-[minmax(65px,85px)] gap-[22px] h-full"}  `,
      },
      form: {
        default: `[--formHeight:413px] h-[var(--formHeight)] items-center  w-full [&_*]:max-h-[var(--formHeight)]  [&:not(:has(input[type=radio]))]:bg-white [&:not(:has(input[type=radio]))]:border [&:not(:has(input[type=radio]))]:border-grey-4  ${
          breakpoints.minTablet
            ? "[&:not(:has(input[type=radio]))]:p-[58px]"
            : "[&:not(:has(input[type=radio]))]:[padding:71px_30px]"
        }`,
      },
      label: {
        default: `caption font-variant-numeric--primary leading-[27px] text-[19px] text-left text-grey-7 -tracking-[.38px]`,
      },
    },
    range: {
      form: {
        default: `bg-white border border-grey-4 flex flex-col h-full justify-center ${
          breakpoints.minTablet ? "p-[58px]" : "[padding:71px_30px]"
        } `,
      },
      label: {
        default: `leading-[20px] text-[15px] text-[#0C1112]`,
      },
      minMaxLabel: {
        default: `font-normal eading-[normal] text-[12px] text-[#0C1112]`,
      },
      undecidedLabel: {
        default: `font-variant-numeric--primary leading-[15px] text-[15px] text-grey-6`,
      },
    },
  }

  const tooltipAttributes = {
    inputGroup: { placement: "right" },
  }

  const radioButtonOnClick = e => {
    const input = e.target.querySelector("input")

    if (input)
      setValue(input.name, input.value, {
        shouldValidate: true,
        shouldTouch: true,
      })
  }
  const radioInputStyle = {
    "--backgroundImage": `url(data:image/svg+xml,${encodeURIComponent(
      renderToString(<Check color={"#fff"} width={20} height={20} />),
    )})`,
  }

  const radioInputAttributes = {
    style: radioInputStyle,
  }

  const radioInputClasses = {
    classes: {
      default: classes.radio.formElement.default,
    },
  }

  const funnelSteps = [
    {
      formSide: {
        heading: {
          text: `Tell us a little about yourself`,
        },
        form: {
          classes: {
            default: `bg-white border border-grey-4 h-full  ${
              breakpoints.minTablet ? "p-[58px]" : "[padding:71px_30px]"
            } `,
          },
        },
        fieldsContainer: {
          classes: {
            default: `flex flex-col gap-[20px] max-w-[338px] w-full`,
          },
        },
        fields: [
          {
            formElement: {
              attributes: {
                name: "first_name",
                type: "text",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "First Name",
            },
          },
          {
            formElement: {
              attributes: {
                name: "last_name",
                type: "text",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Last Name",
            },
          },
          {
            formElement: {
              attributes: {
                name: "email",
                type: "email",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Email",
            },
          },
          {
            formElement: {
              attributes: {
                name: "phone_cell",
                type: "phone",
              },
              classes: {
                ...classes.contact.formElement,
              },

              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Phone number",
            },
          },
          {
            formElement: {
              attributes: {
                name: "terms",
                type: "checkbox",
              },
              type: "input",
              validationRules: {
                required: {
                  value: true,
                  message: `Please read the terms and conditions and check the box to agree.`,
                },
              },
            },
            formElementGroup: {
              classes: {
                default: `bg-grey-1 [padding:8px_12px] w-fit`,
              },
            },
            label: {
              classes: {
                default: `text-grey-7`,
              },
              text: (
                <>
                  I have read and agree to the{" "}
                  <button
                    className="text-pacific-blue"
                    onClick={() => setIsModalOpen(true)}
                    type="button"
                  >
                    terms and conditions
                  </button>
                </>
              ),
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Knowing the best way to reach you helps us offer refinance
              guidance that’s tailored to your timeline, your questions, and
              your financial goals. We're here to help make the most of your
              mortgage—every step of the way!
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.HelloThere.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "contact_info",
    },
    {
      formSide: {
        heading: { text: `Why are you considering a refinance?` },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "lower_monthly_payment",
                      value: "lower my monthly payment",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Lower my monthly payment",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "debt_consolidation",
                      value: "debt consolidation",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Debt consolidation",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "pay_mortgage_faster",
                      value: "pay off my mortgage faster",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Pay off my mortgage faster",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "tap_into_home_equity",
                      value: "tap into my home’s equity for cash",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Tap into my home’s equity for cash",
                  },
                },
              ],
              name: "reason_for_refinance",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Tell us what you're hoping to achieve so we can help you explore
              the refinance options that make the most sense for your home and
              your future.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.YourWay.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "reason_for_refinance",
    },
    {
      formSide: {
        heading: { text: `Where is your home located?` },
        form: {
          classes: {
            default: classes.range.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: ``,
          },
        },
        fields: [
          {
            formElement: {
              attributes: {
                name: "state_of_home_purchase",
                placeholder: "Select a state",
              },
              conditions: [
                {
                  values: Object.keys(citiesByState),
                  operator: "none",
                  matchCondition: "eq",
                  fieldName: "state_of_home_purchase",
                },
              ],
              options: Object.keys(formLeadRecipients).map(state => ({
                label: state,
                value: state,
              })),
              type: "select",
              validationRules: {
                ...requiredOptionRule,
              },
            },
          },
          {
            formElement: {
              attributes: {
                name: "city_of_home_purchase",
                placeholder: "Select the city closest to you",
              },
              conditions: [
                {
                  values: Object.keys(citiesByState),
                  operator: "or",
                  matchCondition: "eq",
                  fieldName: "state_of_home_purchase",
                  resetFieldsObject: {
                    city_of_home_purchase: null,
                  },
                },
              ],
              optionFieldName: "state_of_home_purchase",
              optionsByFieldValue: citiesByState,
              type: "select",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            resetButton: {
              resetFieldsObject: {
                state_of_home_purchase: null,
              },
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              The more we know about your property’s location, the better we can
              shape our recommendations. From area valuations to local
              conditions, we'll help pair you with the ideal refinance solution.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.PointUsHome.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "state_of_home_purchase",
    },
    {
      formSide: {
        heading: {
          text: `Was your existing mortgage handled by an Atlantic Bay Mortgage Banker?`,
        },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              conditions: [
                {
                  values: ["1"],
                  operator: "none",
                  matchCondition: "eq",
                  fieldName: "previous_abmg_client",
                },
              ],
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "previous_abmg_client_yes",
                      value: "1",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Yes",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "previous_abmg_client_no",
                      value: "0",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "No",
                  },
                },
              ],
              name: "previous_abmg_client",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
          {
            formElement: {
              attributes: {
                name: "mb_name",
                placeholder: `Type a name or anything you remember`,
              },
              conditions: [
                {
                  values: ["1"],
                  operator: "and",
                  matchCondition: "eq",
                  fieldName: "previous_abmg_client",
                  resetFieldsObject: {
                    mb_name: null,
                  },
                },
              ],

              options: sortAlphabetically(
                bankersInMBSearch.map(banker => ({
                  label: banker.name,
                  value: banker.slug,
                })),
                "label",
                "ASC",
              ),

              type: "datalist",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            helpElement: {
              text: (
                <>
                  Not sure of the name? Share anything you remember (e.g. first
                  name, where you met, or details that make them stand out){" "}
                </>
              ),
            },
            resetButton: {
              resetFieldsObject: {
                previous_abmg_client: null,
              },
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              This helps us understand your starting point. If Atlantic Bay
              currently services your mortgage, we’ll connect you back to your
              Mortgage Banker. If not, we’ll introduce you to the right expert
              to review your options and make sure refinancing feels clear and
              simple.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.PeopleFirstAlways.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "previous_abmg_client",
    },
    {
      formSide: {
        heading: { text: `What’s your preferred method of contact?` },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "phone",
                      value: "call",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Phone call",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "text_message",
                      value: "text",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Text message",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "contact_email",
                      value: "email",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Email",
                  },
                },
              ],
              name: "preferred_method_of_contact",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Let us know the best way to contact you about your application.
              We'll respect your preference and keep communication simple and
              convenient.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.RingRing.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "preferred_method_of_contact",
    },
  ]

  const navigateFunnelGaProps = isValid
    ? {
        "data-ga-trigger-event": "navigate_funnel",
        "data-ga-funnel_name": "Prequalify (Refinance)",
        "data-ga-lead_source": "Corp-Web-PrequalRefi",
        "data-ga-current_step": funnelSteps[funnelData.currentStep].stepName,
      }
    : {}

  return (
    <>
      <FunnelStepScreen
        reactHookFormProps={reactHookFormProps}
        updateFunnelData={updateFunnelData}
        deleteSavedData={() =>
          typeof window !== "undefined" &&
          localStorage.removeItem(storedFunnelName)
        }
        gaProps={{ navigateFunnel: navigateFunnelGaProps }}
        {...funnelSteps[funnelData.currentStep]}
        formData={funnelData.formData}
        passedSteps={funnelData.passedSteps}
        currentStep={funnelData.currentStep}
        totalSteps={funnelSteps.length}
        startPath={props.location.state?.startPath}
        breakpoints={breakpoints}
      />
      <Modal isModalOpen={isModalOpen} closeModal={() => setIsModalOpen(false)}>
        <ConsentPopup
          updateConsentFieldValue={newValue =>
            setValue("terms", newValue, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            })
          }
          closeModal={() => setIsModalOpen(false)}
        />
      </Modal>
    </>
  )
}

/* -------------------------------- Meta Tags ------------------------------- */
export const Head = props => {
  return (
    <SEO
      title={`Refinance Pre-Qualification | Lower Your Mortgage Rate`}
      description={`Complete our simple refinance form to check rates, lower your payment, or tap into home equity—fast, easy, and no obligation.`}
      ogImage={`https://images.ctfassets.net/n1no4gieqp7s/1D4E8YHDAuaSKX6HCmruF9/9ae48b9f1988154a65195e6bfb4aded8/refinance-prequalify-og.png`}
      slug={props.location.pathname.replace(/^\//, "").replace(/\/$/, "")}
    />
  )
}
